/**
 * Teilnehmerseite (NC-1101, Feat 2.0 #1090) — public route /teilnehmer.
 *
 * Anonymous: no Layout, no AuthContext, no SWA login. The magic link carries
 * `#<runId>.<token>` in the URL fragment; the token is sent per request in the
 * x-participant-token header (participantService). Phases:
 *   pre    — Vorperiode: Kurs-Infos, Zugangsdaten, RDP-Datei
 *   during — Schulung:   dito + Unterlagen-Hinweis
 *   post   — Nachperiode: Projektdateien-Download mit Countdown
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SchoolIcon from '@mui/icons-material/School';
import { participantApi, resolveParticipantToken } from '../services/participantService';
import { ParticipantInfoPayload } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { downloadTextFile } from '../utils/downloadTextFile';
import { addressHint } from '../utils/connectionHint';

const PHASE_LABEL: Record<ParticipantInfoPayload['phase'], string> = {
  pre: 'Vor der Schulung',
  during: 'Schulung läuft',
  post: 'Schulung beendet',
};

function remainingDays(untilIso: string): number {
  const ms = new Date(untilIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export default function ParticipantPage() {
  const [token, setToken] = useState<string | null>(null);
  const [info, setInfo] = useState<ParticipantInfoPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const t = resolveParticipantToken(window.location.hash);
    setToken(t);
    if (!t) {
      setError('Kein Zugangslink gefunden. Bitte den vollständigen Link aus der Einladung verwenden.');
      setLoading(false);
      return;
    }
    participantApi
      .info(t)
      .then(setInfo)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRdpDownload = useCallback(() => {
    if (!info?.connection?.rdpFileContent) return;
    downloadTextFile(`Schulung-${info.runId}.rdp`, info.connection.rdpFileContent, 'application/x-rdp');
  }, [info]);

  const handleProjectDownload = useCallback(async () => {
    if (!token) return;
    setDownloading(true);
    try {
      const dl = await participantApi.projectFileDownload(token);
      window.location.assign(dl.downloadUrl);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  }, [token]);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SchoolIcon color="primary" fontSize="large" />
          <Typography variant="h5" component="h1">
            Nupis Campus — Ihre Schulung
          </Typography>
        </Stack>

        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && info && (
          <>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">{info.course ?? 'Schulung'}</Typography>
                  <Chip label={PHASE_LABEL[info.phase]} color={info.phase === 'during' ? 'success' : 'default'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Hallo {info.participant.displayName}
                  {info.startDateTimeUtc || info.expireOn ? (
                    <>
                      {' — '}
                      {info.startDateTimeUtc ? `Beginn: ${formatDateSafe(info.startDateTimeUtc)}` : ''}
                      {info.endDateTimeUtc || info.expireOn
                        ? ` · Ende: ${formatDateSafe(info.endDateTimeUtc ?? info.expireOn ?? '')}`
                        : ''}
                    </>
                  ) : null}
                </Typography>
              </CardContent>
            </Card>

            {info.phase !== 'post' && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Ihre Schulungs-VM
                  </Typography>
                  {info.connection && info.connection.address ? (
                    <Stack spacing={1.5}>
                      <Typography variant="body2">
                        Verbindung per Remote Desktop (mstsc): <code>{info.connection.address}:{info.connection.rdpPort}</code>
                      </Typography>
                      {addressHint(info.connection.addressKind) && (
                        <Alert severity="info" data-testid="participant-address-hint">
                          {addressHint(info.connection.addressKind)}
                        </Alert>
                      )}
                      <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleRdpDownload}>
                        RDP-Datei herunterladen
                      </Button>
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      Die VM ist noch nicht bereitgestellt. Bitte kurz vor Schulungsbeginn erneut vorbeischauen.
                    </Alert>
                  )}

                  {info.credentials && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Anmeldedaten
                      </Typography>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          Benutzer: <code>{info.credentials.username}</code>
                          <Tooltip title="Benutzernamen kopieren">
                            <IconButton
                              size="small"
                              aria-label="Benutzernamen kopieren"
                              onClick={() => navigator.clipboard.writeText(info.credentials!.username)}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <Typography variant="body2">
                          Passwort: <code>{showPassword ? info.credentials.password : '••••••••••'}</code>
                          <Tooltip title={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}>
                            <IconButton
                              size="small"
                              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                              onClick={() => setShowPassword((v) => !v)}
                            >
                              {showPassword ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Passwort kopieren">
                            <IconButton
                              size="small"
                              aria-label="Passwort kopieren"
                              onClick={() => navigator.clipboard.writeText(info.credentials!.password)}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      </Stack>
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Alert severity="info" icon={false}>
                    {info.materialsHint}
                  </Alert>
                </CardContent>
              </Card>
            )}

            {info.phase === 'post' && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Ihre Projektdateien
                  </Typography>
                  {info.projectFiles.available ? (
                    <Stack spacing={1.5}>
                      <Typography variant="body2">
                        Ihre in der Schulung erstellten Dateien stehen noch{' '}
                        <strong>{remainingDays(info.accessUntilUtc)} Tag(e)</strong> zum Download bereit
                        {info.projectFiles.sizeBytes
                          ? ` (${(info.projectFiles.sizeBytes / (1024 * 1024)).toFixed(1)} MB)`
                          : ''}
                        . Danach werden sie automatisch gelöscht.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={downloading ? <CircularProgress size={18} /> : <DownloadIcon />}
                        disabled={downloading}
                        onClick={handleProjectDownload}
                      >
                        Projektdateien herunterladen (ZIP)
                      </Button>
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      Es wurden keine Projektdateien eingesammelt (der Ordner war leer oder die Einsammlung steht noch aus).
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            <Typography variant="caption" color="text.secondary" align="center">
              Diese Seite ist bis {formatDateSafe(info.accessUntilUtc)} erreichbar. Bei Fragen wenden Sie sich an Ihre
              Trainerin / Ihren Trainer.
            </Typography>
          </>
        )}
      </Stack>
    </Container>
  );
}
